import React, { useEffect, useMemo, useState } from 'react';
import NavigationBar from '../core/navigation_bar/navigation_bar';
import Footer from '../core/footer/footer';
import { pageHeader, authHeaders, formatDisplayDateTime } from '../global_vars';
import RichTextDisplay from '../common/RichTextDisplay';
import RichTextEditor from '../common/RichTextEditor';
import axios from 'axios';
import { Modal, Button, Form } from 'react-bootstrap';
import GuardedModal from "../common/GuardedModal";
import Swal from 'sweetalert2';
import { FaWrench } from 'react-icons/fa';

const API_BASE = "https://ultima.icsgr.com";

function apiErrorText(e, fallback) {
  const status = e?.response?.status;
  const data = e?.response?.data;
  const detail =
    (typeof data === 'string' && data) ||
    data?.error ||
    data?.detail ||
    (data && JSON.stringify(data)) ||
    e?.message ||
    fallback;
  if (status === 401) {
    return `(401) Session expired or not logged in — please log out and log in again. ${detail}`;
  }
  return status ? `(${status}) ${detail}` : String(detail);
}

export default function Tools() {
  const [posts, setPosts] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const token = localStorage.getItem('userToken');
    if (!token) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: '(401) Not logged in — open Login and sign in, then reopen Tools.',
      });
      setPosts([]);
      setLoaded(true);
      return;
    }
    try {
      const res = await axios.get(API_BASE + '/api/dashboard/tool_posts/', { headers: authHeaders() });
      setPosts(res?.data?.tool_posts || []);
      setCanManage(!!res?.data?.can_manage);
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: apiErrorText(e, 'Failed to load tools'),
      });
      setPosts([]);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [posts]);

  const filtered = useMemo(() => {
    if (!categoryFilter) return posts;
    return posts.filter((p) => (p.category || '') === categoryFilter);
  }, [posts, categoryFilter]);

  const createPost = async () => {
    if (!title.trim()) {
      Swal.fire({ icon: 'warning', title: 'Title required' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(API_BASE + '/api/dashboard/tool_posts/', {
        title: title.trim(),
        category: category.trim(),
        body,
        active: true,
      }, { headers: authHeaders() });
      setShowModal(false);
      setTitle('');
      setCategory('');
      setBody('');
      await load();
      Swal.fire({ icon: 'success', title: 'Post created', timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e?.response?.data?.error || 'Failed to create post',
      });
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Remove this post?',
      showCancelButton: true,
      confirmButtonText: 'Remove',
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(API_BASE + `/api/dashboard/tool_posts/${id}/`, { headers: authHeaders() });
      await load();
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e?.response?.data?.error || 'Failed to remove',
      });
    }
  };

  return (
    <>
      <NavigationBar />
      <div className="mainContainer">
        <div className="contentContainer">
          <div className="contentBody">
            {pageHeader('tools', 'Tools')}
            <div className="container">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FaWrench style={{ color: '#93ab3c' }} />
                  <span style={{ color: '#666' }}>Useful links and internal posts (newest first)</span>
                  {categories.length ? (
                    <Form.Control
                      as="select"
                      size="sm"
                      style={{ width: 220 }}
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="">All categories</option>
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Form.Control>
                  ) : null}
                </div>
                {canManage ? (
                  <button type="button" className="btn btn-sm btn-success" onClick={() => setShowModal(true)}>
                    Add post
                  </button>
                ) : null}
              </div>

              {!loaded ? (
                <div>Loading...</div>
              ) : filtered.length === 0 ? (
                <div>No posts yet.</div>
              ) : (
                filtered.map((p) => (
                  <div key={p.toolpost_id} className="card" style={{ marginBottom: 14 }}>
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{p.title}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {formatDisplayDateTime(p.created_at)}
                          {p.category ? ` · ${p.category}` : ''}
                          {p.created_by_username ? ` · ${p.created_by_username}` : ''}
                        </div>
                      </div>
                      {canManage ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deletePost(p.toolpost_id)}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="card-body">
                      <RichTextDisplay html={p.body} emptyLabel="" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <GuardedModal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>New tools post</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Control
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              maxLength={60}
              placeholder="e.g. Links, How-to, Internal"
              list="tool-category-suggestions"
            />
            <datalist id="tool-category-suggestions">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Form.Group>
          <Form.Group>
            <Form.Label>Content</Form.Label>
            <RichTextEditor value={body} onChange={setBody} placeholder="Links, instructions, notes…" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="success" disabled={saving} onClick={createPost}>
            {saving ? 'Saving…' : 'Publish'}
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <Footer />
    </>
  );
}
